// ════════════════════════════════════════════════════════════
// Toaster — Provider Sonner branché sur les tokens ECR
// ════════════════════════════════════════════════════════════
// À monter UNE SEULE FOIS dans apps/web/src/routes/__root.tsx
// (déjà fait — pas besoin de l'instancier ailleurs).
//
// Spec :
//   - Position responsive : desktop bottom-right / mobile top-center
//     (style notifications iOS/Android natives).
//   - Stack collapsed par défaut, expand au hover (Sonner natif).
//   - Swipe-to-dismiss adapté à la position (haut → swipe up, bas →
//     swipe down).
//   - Durée par défaut 4 s (loading = ∞ tant que la promise n'est pas
//     résolue).
//
// ⚠️ richColors désactivé : on map les variants sur nos tokens
// sémantiques (success/warning/destructive/info subtle + emphasis)
// via toastOptions.classNames. richColors mettrait du green/red brut
// qui ne respecterait pas la rampe brand ECR ni le dark mode.
//
// ⚠️ Bug fix 2026-05-29 (run 4) — toast invisible sur iPhone 14 Pro Max
// Chrome DevTools. Vrai diagnostic enfin (après 3 fausses pistes) :
//
// Sur certains environnements (notamment Chrome DevTools en mode iPhone
// 14 Pro Max), `window.innerWidth` (= visual viewport, 837 px) ≠
// `documentElement.clientWidth` (= layout viewport, 430 px). Sonner
// applique `[data-sonner-toaster] { width: 100% }` en mobile, et
// `100%` se résout sur la VISUAL viewport (837 px). Résultat : le
// toast fait 805 px de large alors que seuls 430 px sont visibles → la
// quasi-totalité du toast déborde hors de la zone visible.
//
// Preuve diagnostic Sidi (29/05/2026) :
//   - OL rect.width = 836.67 px, left = 16 px
//   - window.innerWidth = 837, clientWidth = 430
//   - opacity:1, visibility:visible, dans la viewport (en visual)
//   - elementFromPoint au centre du toast retourne bien le LI
//   - matchMedia (max-width:600px) fires (donc règle mobile Sonner active)
//   - Aucun ancestor avec transform/filter (pas de containing block override)
//
// Fix : on cap la largeur via `style={{ maxWidth: 'X' }}` inline. L'inline
// style bat la règle CSS `width: 100%` de Sonner (sans !important). Le
// toast est cappé à 380 px max → toujours dans la layout viewport 430 px
// même sur le quirk DevTools, et reste centré naturellement sur les
// vrais devices ≥ 380 px.
//
// Bonus : mobileOffset.top = 60 reste utile pour clearer une éventuelle
// Dynamic Island réelle. swipeDirections adapté à la position.
// ════════════════════════════════════════════════════════════
import { Toaster as SonnerToaster } from 'sonner';
import { useIsMobile } from '@/components/ui/table/hooks/use-media-query';

// Object of NUMBERS — Sonner v1.7 valide ce format (cf. fonction Te()
// dans node_modules/sonner/dist/index.mjs).
// top: 60 = au-dessus d'une éventuelle Dynamic Island iPhone 14 Pro Max.
const MOBILE_OFFSET = {
  top: 60,
  right: 16,
  bottom: 16,
  left: 16,
} as const;

// Cap la largeur du toaster en mobile pour neutraliser le quirk Chrome
// DevTools `window.innerWidth ≠ clientWidth`. 380 px = standard mobile
// toast width (Linear / Vercel / Notion). Reste centré dans la viewport
// visible même sur le quirk + assez large pour héberger un titre +
// description sans wrap excessif.
const MOBILE_STYLE = {
  width: '380px',
  maxWidth: 'calc(100vw - 32px)',
} as const;

/**
 * Toaster ECR — wrapper Sonner branché sur les tokens design system.
 *
 * @remarks
 * - Position responsive : `bottom-right` desktop / `top-center` mobile (<768 px).
 * - 6 variants stylés via les tokens sémantiques :
 *   `success` / `error` / `warning` / `info` / `loading` / `default`.
 * - À monter UNE SEULE FOIS à la racine — multi-mount = stacks dupliqués.
 */
export function Toaster() {
  const isMobile = useIsMobile();
  return (
    <SonnerToaster
      position={isMobile ? 'top-center' : 'bottom-right'}
      closeButton
      duration={4000}
      // Object of numbers (validé Sonner v1.7) — `top: 60` clear la
      // Dynamic Island iPhone 14 Pro Max, autres côtés à 16 px.
      mobileOffset={MOBILE_OFFSET}
      // Cap la largeur en mobile (cf. doc supra — quirk Chrome DevTools
      // visual viewport ≠ layout viewport). L'inline style bat la règle
      // CSS `width: 100%` de Sonner sans !important.
      style={isMobile ? MOBILE_STYLE : undefined}
      // Swipe-to-dismiss aligné sur la position : haut → swipe up,
      // bas → swipe down. Sans ça, Sonner v1.7 conserve la direction
      // par défaut (down) même quand la position est en haut, ce qui
      // empêche de fermer un toast top-center par geste.
      swipeDirections={isMobile ? ['top'] : ['bottom', 'right']}
      // Override systématique : on n'utilise PAS richColors (cf. doc supra).
      // Toutes les classes sont mappées sur les tokens sémantiques ECR
      // pour garder la cohérence light/dark + la rampe brand.
      toastOptions={{
        classNames: {
          // Shell commun — bordure visible, ombre franche, rayon md.
          toast:
            'group rounded-md border shadow-lg font-sans backdrop-blur-sm ' +
            'data-[type=success]:bg-success-subtle data-[type=success]:text-success-emphasis data-[type=success]:border-success/30 ' +
            'data-[type=error]:bg-destructive-subtle data-[type=error]:text-destructive-emphasis data-[type=error]:border-destructive/30 ' +
            'data-[type=warning]:bg-warning-subtle data-[type=warning]:text-warning-emphasis data-[type=warning]:border-warning/30 ' +
            'data-[type=info]:bg-info-subtle data-[type=info]:text-info-emphasis data-[type=info]:border-info/30 ' +
            'data-[type=loading]:bg-card data-[type=loading]:text-card-foreground data-[type=loading]:border-border ' +
            'data-[type=default]:bg-card data-[type=default]:text-card-foreground data-[type=default]:border-border',
          title: 'text-sm font-semibold leading-tight',
          description: 'text-sm opacity-80 leading-snug',
          actionButton:
            'bg-primary text-primary-foreground hover:brightness-95 ' +
            'rounded-md px-3 py-1 text-xs font-medium shadow-sm transition-[filter] duration-150 motion-reduce:transition-none',
          cancelButton:
            'bg-muted text-muted-foreground hover:bg-muted/80 ' +
            'rounded-md px-3 py-1 text-xs font-medium transition-colors duration-150 motion-reduce:transition-none',
          closeButton:
            'border-current/20 bg-transparent hover:bg-black/5 dark:hover:bg-white/5 ' +
            'transition-colors duration-150 motion-reduce:transition-none',
          icon: 'shrink-0',
        },
      }}
    />
  );
}
